import { FinalImpactScoreType } from '@upright/common/model/output/scores/types';
import { Dimension, Valence } from '@upright/common/model/types';
import { getImpactBarDataLabelId } from '@upright/test-ids/dom-elements';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { dimensionBarsLayout } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/dimension-bars-layout';
import { valenceToSide } from '@upright/ui/common/upright/core/impact-lens-profile/common/utils';
import { useCarouselLayout } from '@upright/ui/common/upright/core/impact-lens-profile/context';
import { useNetImpactLensContext } from '@upright/ui/common/upright/core/impact-lenses/net-impact/common/use-net-impact-lens-context';
import { signForValenceAndValue } from '@upright/ui/common/upright/model/output/scores/net-impact-chart/shared';
import { formatImpactScore } from '@upright/ui/common/upright/model/output/scores/net-impact-chart/value-formatter';
import { isNil } from 'lodash';
import styled from 'styled-components';

interface Props {
  dimension: Dimension;
  valence: Valence;
}

/**
 * Number drawn inside the bar if the bar is long enough
 */
export function DimensionBarDataLabel(props: Props) {
  const baseLayout = useCarouselLayout();
  const context = useNetImpactLensContext();
  if (!context) {
    return <></>;
  }
  const { formatters, scoreUnit = FinalImpactScoreType.RELATIVE, scoreReader } = context.options;
  const value = scoreReader.readScore(undefined, props.dimension, undefined, props.valence);
  const data = context.data?.scores;
  if (isNil(value) || !data) {
    return <></>;
  }
  // `ghostScores` must match what `DimensionBar` passes — it feeds the shared
  // `maxX`, and `dimensionBarAndLabelArea` extends past the ghost tail so the
  // label is never drawn on top of it.
  const layout = dimensionBarsLayout(baseLayout, data, undefined, context.data?.ghostScores);

  const margin = 4;
  const barArea = layout.dimensionBarAndLabelArea(valenceToSide(props.valence), props.dimension);

  const x = props.valence === Valence.POSITIVE ? barArea.x2 + margin : barArea.x1 - margin;
  const anchor = props.valence === Valence.POSITIVE ? 'start' : 'end';

  const color = BouColor.PLATFORM_TEXT_GRAY;

  const testId = getImpactBarDataLabelId(props.dimension, props.valence);

  return (
    <Text fill={color} x={x} textAnchor={anchor} y={barArea.cy} dy="0.35em" data-testid={testId}>
      {signForValenceAndValue(props.valence, value)}
      {formatImpactScore(value, formatters, scoreUnit)}
    </Text>
  );
}

const Text = styled.text``;
